import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import API_BASE, { withApiCredentials } from "../api";
import {
  getActiveAwareAuthToken,
  getLegacyAppToken,
} from "../auth/bootstrapTokens";
import "../styles/components/FriendNotifications.css";
import FriendNotificationList from "./friend-notifications/FriendNotificationList";
import FriendRequestList from "./friend-notifications/FriendRequestList";
import {
  formatNotificationTimestamp,
  getBellNotifications,
  getLatestFriendRequestNotificationsByUserId,
  getNotificationAvatarSrc,
  sortNotificationsByCreatedAt,
} from "./friend-notifications/friendNotificationUtils";
import useNotificationState from "./friend-notifications/useNotificationState";

const NOTIFICATION_POPUP_ID = "friend-notifications-popup";

function isIphoneBrowser() {
  if (typeof navigator === "undefined") return false;
  return /iPhone/i.test(navigator.userAgent || "");
}
export default function FriendNotifications({
  currentUser = null,
  users = [],
  onOpenNotificationPost = null,
  bellIcon = null,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const [mobileFilter, setMobileFilter] = useState("friendRequests");
  const [usersMap, setUsersMap] = useState({});
  const wrapperRef = useRef(null);

  const token = getLegacyAppToken(window);
  const authReadToken = getActiveAwareAuthToken(window);
  const groupActionToken = authReadToken || token;
  const isiPhone = isIphoneBrowser();
  const requests = Array.isArray(currentUser?.friendRequestsReceived)
    ? currentUser.friendRequestsReceived
    : [];

  const {
    actionError,
    loading,
    markAllRead,
    markOneRead,
    markingAll,
    notifications,
    refreshList,
    unreadCount,
  } = useNotificationState({
    userId: currentUser?.id,
    authToken: authReadToken,
    open,
  });

  useEffect(() => {
    if (!Array.isArray(users) || users.length === 0) return;
    const nextUsersMap = {};
    users.forEach((loadedUser) => {
      if (loadedUser?.id) nextUsersMap[loadedUser.id] = loadedUser;
    });
    setUsersMap(nextUsersMap);
  }, [users]);

  useEffect(() => {
    if (!open || Object.keys(usersMap).length > 0) return;

    const controller = new AbortController();
    async function loadUsers() {
      try {
        const response = await fetch(`${API_BASE}/auth/users`, {
          signal: controller.signal,
        });
        if (!response.ok) return;
        const loadedUsers = await response.json();
        if (!Array.isArray(loadedUsers)) return;
        const nextUsersMap = {};
        loadedUsers.forEach((loadedUser) => {
          if (loadedUser?.id) nextUsersMap[loadedUser.id] = loadedUser;
        });
        setUsersMap(nextUsersMap);
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load notification users", error);
        }
      }
    }

    loadUsers();
    return () => controller.abort();
  }, [open, usersMap]);

  useEffect(() => {
    setOpen(false);
    setShowAllNotifications(false);
    setMobileFilter("friendRequests");
  }, [currentUser?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setShowAllNotifications(false);
      setMobileFilter("friendRequests");
    }
  }, [open]);

  useEffect(() => {
    if (!open || typeof window === "undefined" || window.innerWidth > 768) {
      return undefined;
    }

    const scrollY = window.scrollY;
    const { style } = document.body;
    const previousBodyStyles = {
      overflow: style.overflow,
      position: style.position,
      top: style.top,
      left: style.left,
      right: style.right,
      width: style.width,
    };

    style.overflow = "hidden";
    style.position = "fixed";
    style.top = `-${scrollY}px`;
    style.left = "0";
    style.right = "0";
    style.width = "100%";

    return () => {
      Object.assign(style, previousBodyStyles);
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    const handleGroupsUpdated = () => refreshList();
    window.addEventListener("groupsUpdated", handleGroupsUpdated);
    return () => window.removeEventListener("groupsUpdated", handleGroupsUpdated);
  }, [refreshList]);

  const authHeaders = groupActionToken
    ? { Authorization: `Bearer ${groupActionToken}` }
    : {};
  const legacyHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const runNotificationAction = async (
    url,
    options,
    { eventName = null } = {}
  ) => {
    const response = await fetch(url, withApiCredentials(options));
    if (!response.ok) return false;
    setOpen(false);
    if (eventName) window.dispatchEvent(new Event(eventName));
    await refreshList();
    return true;
  };

  const acceptSharedTrip = (notificationId) =>
    runNotificationAction(
      `${API_BASE}/trips/shared/${encodeURIComponent(notificationId)}/accept`,
      { method: "POST", headers: authHeaders },
      { eventName: "tripsUpdated" }
    );
  const declineSharedTrip = (notificationId) =>
    runNotificationAction(`${API_BASE}/trips/shared/${encodeURIComponent(notificationId)}/decline`, {
      method: "POST",
      headers: authHeaders,
    });
  const acceptGroupInvite = (notificationId) =>
    runNotificationAction(
      `${API_BASE}/groups/invites/${encodeURIComponent(notificationId)}/accept`,
      { method: "POST", headers: authHeaders },
      { eventName: "groupsUpdated" }
    );
  const declineGroupInvite = (notificationId) =>
    runNotificationAction(
      `${API_BASE}/groups/invites/${encodeURIComponent(notificationId)}/decline`,
      { method: "POST", headers: authHeaders },
      { eventName: "groupsUpdated" }
    );
  const approveGroupJoinRequest = (notificationId) =>
    runNotificationAction(
      `${API_BASE}/groups/requests/${encodeURIComponent(notificationId)}/approve`,
      { method: "POST", headers: authHeaders },
      { eventName: "groupsUpdated" }
    );
  const rejectGroupJoinRequest = (notificationId) =>
    runNotificationAction(
      `${API_BASE}/groups/requests/${encodeURIComponent(notificationId)}/reject`,
      { method: "POST", headers: authHeaders },
      { eventName: "groupsUpdated" }
    );
  const approvePendingGroupPost = (groupId, postId) =>
    runNotificationAction(
      `${API_BASE}/groups/${encodeURIComponent(groupId)}/posts/${encodeURIComponent(postId)}/approve`,
      { method: "POST", headers: legacyHeaders },
      { eventName: "groupsUpdated" }
    );
  const rejectPendingGroupPost = (groupId, postId) =>
    runNotificationAction(
      `${API_BASE}/groups/${encodeURIComponent(groupId)}/posts/${encodeURIComponent(postId)}/reject`,
      { method: "POST", headers: legacyHeaders },
      { eventName: "groupsUpdated" }
    );

  const friendRequestNotificationsByUserId =
    getLatestFriendRequestNotificationsByUserId(notifications);

  const actOnFriendRequest = async (action, userId) => {
    const formData = new FormData();
    if (token) formData.append("token", token);
    formData.append("from_user_id", userId);
    const response = await fetch(
      `${API_BASE}/friends/${action}`,
      withApiCredentials({ method: "POST", body: formData })
    );
    if (!response.ok) return false;

    const relatedNotification = friendRequestNotificationsByUserId[userId];
    if (relatedNotification?.id) await markOneRead(relatedNotification.id);
    setOpen(false);
    await refreshList();
    window.dispatchEvent(new Event("friendsUpdated"));
    return true;
  };

  const bellNotifications = getBellNotifications(notifications);
  const sortedNotifications = sortNotificationsByCreatedAt(bellNotifications);
  const visibleNotifications = showAllNotifications
    ? sortedNotifications
    : sortedNotifications.slice(0, 5);
  const hasHiddenNotifications =
    sortedNotifications.length > 5 && !showAllNotifications;
  const unreadNotifications = sortedNotifications.filter(
    (notification) => !notification.read
  );
  const mobileRecentNotifications =
    mobileFilter === "unread" ? unreadNotifications : sortedNotifications;
  const visibleMobileNotifications = showAllNotifications
    ? mobileRecentNotifications
    : mobileRecentNotifications.slice(0, 6);
  const hasHiddenMobileNotifications =
    mobileRecentNotifications.length > 6 && !showAllNotifications;

  const renderTimestamp = (createdAt) => {
    const label = formatNotificationTimestamp(createdAt);
    return label ? <div className="notif-timestamp">{label}</div> : null;
  };
  const getAvatarSrc = (user) => getNotificationAvatarSrc(user, API_BASE);

  const handleOpenPostNotification = async (notification) => {
    if (!notification?.postId || typeof onOpenNotificationPost !== "function") {
      return;
    }
    await markOneRead(notification.id);
    setOpen(false);
    onOpenNotificationPost(notification);
  };

  const handleOpenSharedLocation = async (notification) => {
    await markOneRead(notification.id);
    sessionStorage.setItem(
      "pendingMapLocation",
      JSON.stringify({ lat: notification.lat, lng: notification.lng })
    );
    window.dispatchEvent(new CustomEvent("navigateToMap"));
    setOpen(false);
  };

  const mobileFilters = [
    {
      key: "friendRequests",
      label: t("notifications_friend_requests"),
      count: requests.length,
    },
    {
      key: "all",
      label: t("notifications_all"),
      count: sortedNotifications.length,
    },
    {
      key: "unread",
      label: t("notifications_unread"),
      count: unreadNotifications.length,
    },
  ];

  const actionErrorMessage =
    actionError === "markAll"
      ? t("notifications_mark_all_error")
      : actionError === "markOne"
        ? t("notifications_mark_one_error")
        : null;

  const listProps = {
    usersMap,
    getAvatarSrc,
    renderTimestamp,
    onOpenPostNotification: handleOpenPostNotification,
    onAcceptSharedTrip: acceptSharedTrip,
    onDeclineSharedTrip: declineSharedTrip,
    onAcceptGroupInvite: acceptGroupInvite,
    onDeclineGroupInvite: declineGroupInvite,
    onApproveGroupJoinRequest: approveGroupJoinRequest,
    onRejectGroupJoinRequest: rejectGroupJoinRequest,
    onApprovePendingGroupPost: approvePendingGroupPost,
    onRejectPendingGroupPost: rejectPendingGroupPost,
    onOpenSharedLocation: handleOpenSharedLocation,
    onShowAll: () => setShowAllNotifications(true),
    t,
  };

  return (
    <div className="notif-wrapper" ref={wrapperRef}>
      <button
        type="button"
        className="notif-bell"
        aria-label={t("notifications_bell_aria_label")}
        aria-expanded={open}
        aria-controls={NOTIFICATION_POPUP_ID}
        onClick={() => setOpen((current) => !current)}
      >
        {bellIcon ? (
          <span className="notif-bell__icon" aria-hidden="true">
            {bellIcon}
          </span>
        ) : (
          <span aria-hidden="true">🔔</span>
        )}
        {unreadCount > 0 ? (
          <span className="notif-count" aria-label={t("notifications_unread_count", { count: unreadCount })}>
            {unreadCount}
          </span>
        ) : null}
      </button>

      {open ? (
        <div
          id={NOTIFICATION_POPUP_ID}
          className={`notif-dropdown${isiPhone ? " notif-dropdown--ios" : ""}`}
          role="region"
          aria-label={t("notifications_popup_aria_label")}
          aria-busy={loading || markingAll}
        >
          <div className="notif-mobile-panel">
            <header className="notif-mobile-hero">
              <button
                type="button"
                className="notif-mobile-close"
                aria-label={t("notifications_close")}
                onClick={() => setOpen(false)}
              >
                <X size={18} strokeWidth={2.35} aria-hidden="true" />
              </button>
              <p className="notif-mobile-eyebrow">{t("notifications_latest")}</p>
              <h2>{t("notifications_title")}</h2>
              <p>{t("notifications_subtitle")}</p>
            </header>

            {unreadCount > 0 ? (
              <div className="notif-toolbar">
                <button
                  type="button"
                  className="notif-mark-all"
                  disabled={markingAll}
                  aria-busy={markingAll}
                  onClick={markAllRead}
                >
                  {markingAll
                    ? t("notifications_marking_all_read")
                    : t("notifications_mark_all_read")}
                </button>
              </div>
            ) : null}

            {actionErrorMessage ? (
              <div className="notif-action-error" role="alert">
                {actionErrorMessage}
              </div>
            ) : null}

            <div
              className="notif-mobile-filters"
              role="tablist"
              aria-label={t("notifications_filter_aria")}
            >
              {mobileFilters.map((filter) => (
                <button
                  type="button"
                  key={filter.key}
                  className={`notif-mobile-filter${
                    mobileFilter === filter.key
                      ? " notif-mobile-filter--active"
                      : ""
                  }`}
                  aria-pressed={mobileFilter === filter.key}
                  onClick={() => {
                    setMobileFilter(filter.key);
                    setShowAllNotifications(false);
                  }}
                >
                  <span>{filter.label}</span>
                  <strong>{filter.count}</strong>
                </button>
              ))}
            </div>

            <FriendRequestList
              mobile
              requests={requests}
              usersMap={usersMap}
              friendRequestNotificationsByUserId={
                friendRequestNotificationsByUserId
              }
              getAvatarSrc={getAvatarSrc}
              renderTimestamp={renderTimestamp}
              onAccept={(userId) => actOnFriendRequest("accept", userId)}
              onDecline={(userId) => actOnFriendRequest("decline", userId)}
              t={t}
            />

            <FriendNotificationList
              {...listProps}
              mobile
              notifications={visibleMobileNotifications}
              notificationCount={mobileRecentNotifications.length}
              hasHiddenNotifications={hasHiddenMobileNotifications}
            />
          </div>

          <div className="notif-desktop-content">
            <FriendRequestList
              requests={requests}
              usersMap={usersMap}
              friendRequestNotificationsByUserId={
                friendRequestNotificationsByUserId
              }
              getAvatarSrc={getAvatarSrc}
              renderTimestamp={renderTimestamp}
              onAccept={(userId) => actOnFriendRequest("accept", userId)}
              onDecline={(userId) => actOnFriendRequest("decline", userId)}
              t={t}
            />
            <FriendNotificationList
              {...listProps}
              notifications={visibleNotifications}
              hasHiddenNotifications={hasHiddenNotifications}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}
